import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useParams } from 'react-router-dom';
import VideoPlayer from "components/ai/tools/VideoPlayer";
import { SSEEventType } from 'constants/sse';
import CommonHelper from "helpers/CommonHelper";
import useSSEEvent from 'hooks/useSSEEvent';
import ls from 'local-storage';
import { useLazyGetDialogByScenarioAliasQuery } from 'services/api/dialogsApi';
import styled, { css, keyframes } from "styled-components";
import { errorAlert, successAlert } from 'ui-kit/Alert';
import { getErrorMessage } from 'utils/error';

import { useVideoPolling } from './hooks/useVideoPolling';

const CONTAINER_WIDTH = 320;

const GeneratedVideoMessage = (props) => {
  const {
    url,
    videoUrl,
    setVideoModalState,
    setBatches = Function.prototype,
    isNsfw = false,
    onZoomClick
  } = props;

  const { alias } = useParams();
  const [imageRatio, setImageRatio] = useState(null);

  const { data: videoGenerationData } = useSSEEvent(SSEEventType.CUSTOM_DIALOG_VIDEO_GEN);
  const { isGenerating, isInitialCheckComplete, videoCheckRef } = useVideoPolling(videoUrl, isNsfw);

  const [getDialogByAlias] = useLazyGetDialogByScenarioAliasQuery();

  const characterName = ls.get('characterName');

  const handleRefetchDialogData = async () => {
    try {
      const result = await getDialogByAlias({ alias, scenarioId: undefined, characterName, isAdminPanel: false }).unwrap();
      if (result?.batches) {
        setBatches(result.batches);
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      errorAlert(errorMessage);
    }
  };

  useEffect(() => {
    if (videoGenerationData) {
      const hasNsfwContent = Array.isArray(videoGenerationData)
        ? videoGenerationData.some(item => item.isNsfw === true)
        : videoGenerationData.isNsfw === true;

      if (hasNsfwContent) {
        toast.dismiss();
        const errorDescription = 'Video generation isn’t available for images containing exposed genitals or bare breasts. Please try again with another image — lingerie and suggestive content are OK.';
        errorAlert('Content Restricted', errorDescription);
        successAlert('Tokens Refunded', 'Tokens for this attempt were returned to your account.');
        handleRefetchDialogData();
      }
    }
  }, [videoGenerationData]);

  const isPendingVideoGeneration = isGenerating && isInitialCheckComplete && !isNsfw;

  let containerHeight = CONTAINER_WIDTH;

  if (imageRatio) {
    containerHeight = Math.round(CONTAINER_WIDTH / imageRatio);
  }

  const getImageUrl = () => {
    return CommonHelper.getFilesCdnUrl(url);
  };

  const handleImageLoad = (e) => {
    const img = e.target;
    if (img.naturalWidth && img.naturalHeight) {
      setImageRatio(img.naturalWidth / img.naturalHeight);
    } else {
      setImageRatio(1);
    }
  };

  const handleImageClick = () => {
    if (isNsfw && onZoomClick) {
      onZoomClick(url);
    }
  };

  return (
    <MessageContainer>
      <FlexContainer>
        <PictureContainer>

          <video
            ref={videoCheckRef}
            style={{ display: 'none' }}
            preload="metadata"
            muted
          />


          {(isPendingVideoGeneration || isNsfw) && (
            <ImageOnlyWrapper
              width={CONTAINER_WIDTH}
              height={containerHeight}
            >
              <ImagePlaceholder
                isPendingVideoGeneration={isPendingVideoGeneration}
              >
                <ImagePhoto
                  src={getImageUrl()}
                  clickable={isNsfw && onZoomClick}
                />
                {onZoomClick && isNsfw && (
                  <ZoomImageButton onClick={handleImageClick} />
                )}
                <HiddenImage
                  src={getImageUrl()}
                  onLoad={handleImageLoad}
                  onError={handleImageLoad}
                />
              </ImagePlaceholder>
              <PendingVideoInfo isNsfw={isNsfw}>
                <span>Generating your video…</span>
              </PendingVideoInfo>
            </ImageOnlyWrapper>
          )}

          {!isPendingVideoGeneration && !isNsfw && (
            <VideoWrapper
              width={CONTAINER_WIDTH}
              height={containerHeight}
            >
              <VideoPlayer
                isNsfw={isNsfw}
                size="100%"
                imageUrl={url}
                videoUrl={videoUrl}
                setVideoModalState={setVideoModalState}
                onImageLoad={handleImageLoad}
              />
            </VideoWrapper>
          )}
        </PictureContainer>
      </FlexContainer>
    </MessageContainer>
  );
};

export default GeneratedVideoMessage;

const shimmerAnimation = keyframes`
  0% {
    transform: translate(-100%, -100%) rotate(45deg);
    opacity: 0;
  }
  5% {
    opacity: 1;
  }
  47% {
    transform: translate(100%, 100%) rotate(45deg);
    opacity: 1;
  }
  53% {
    transform: translate(100%, 100%) rotate(45deg);
    opacity: 1;
  }
  95% {
    transform: translate(-100%, -100%) rotate(45deg);
    opacity: 1;
  }
  100% {
    transform: translate(-100%, -100%) rotate(45deg);
    opacity: 0;
  }
`;

const MessageContainer = styled.div`
  margin-top: 1.5rem;
`;

const FlexContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const PictureContainer = styled.div`
  position: relative;
  margin-left: auto;
  margin-right: auto;
`;

const VideoWrapper = styled.div`
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  width: ${props => props.width}px;
  height: ${props => props.height}px;
  transition: height 0.2s;
`;

const ImageOnlyWrapper = styled.div`
  padding: 5px;
  border-radius: 12px;
  width: ${props => props.width}px;
  height: ${props => props.height}px;
  transition: height 0.2s;
  margin-bottom: 1.5rem;
  position: relative;
`;

const ImagePlaceholder = styled.div`
  background: black;
  border-radius: 12px;
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;

  ${props => props.isPendingVideoGeneration && css`
    &::after {
      content: '';
      position: absolute;
      top: -50%;
      left: -50%;
      width: 200%;
      height: 200%;
      background: linear-gradient(
        90deg,
        transparent 0%,
        transparent 40%,
        rgba(255, 255, 255, 0.04) 47%,
        rgba(255, 255, 255, 0.07) 50%,
        rgba(255, 255, 255, 0.04) 53%,
        transparent 60%,
        transparent 100%
      );
      transform: translate(-100%, -100%) rotate(45deg);
      animation: ${shimmerAnimation} 4s cubic-bezier(0.4, 0.0, 0.2, 1) infinite;
      pointer-events: none;
    }
  `}
`;

const ImagePhoto = styled.div`
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  background-image: url("${props => props.src}");
  border-radius: 12px;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: ${props => props.clickable ? 'pointer' : 'default'};
`;

const ZoomImageButton = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 12px;
  cursor: pointer;
  z-index: 2;
`;

const HiddenImage = styled.img`
  position: absolute;
  visibility: hidden;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
`;

const PendingVideoInfo = styled.span`
position: absolute;
  bottom: -30px;
  right: 0;
  border-radius: 100px;
  display: flex;
  align-items: center;
  gap: 4px;
  border: none;
  ${props => props.isNsfw && css`
    display: none;
  `}

  span {
  color: white;
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px;
  background: linear-gradient(135deg, #9E5EEE 0%, #B759EA 32%, #C65DE9 66%, #B885F4 100%);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  }
`;